/**
 * Item Market and bazaar ledgers from the player's own log and listings (website roadmap S26 — torn.report's Item
 * Market and Bazaar pages), on the log-report kit (log-kit.ts; the browser half is apps/web/src/app/log-kit/). Pure and
 * browser-safe; tested in packages/shared/test/trading-report.test.ts.
 *
 * - The log (Full key): "Item market buy/sell" and "Bazaar buy/sell" lines. Torn doesn't document their `data`, so they
 *   are read leniently: the item(s) and amounts from `items` (or `item` + `quantity`), the price from `cost_each`
 *   (or the total over the amount), the total from `cost_total`, the Item Market's fee from `fee`, the other player from
 *   `seller`/`buyer`. What a line doesn't say stays unknown (null), never guessed.
 * - Your Item Market listings: Torn v2 `/user/itemmarket` (Limited key) — `itemmarket: [{ id, item: { id, name, type },
 *   price, average_price, amount, available, is_anonymous }]`, price per item.
 * - Your bazaar: Torn v1 `/user/?selections=bazaar` (any key) — `bazaar: [{ ID, UID, name, type, quantity, price,
 *   market_price }]`.
 * Nothing here goes anywhere: the website keeps the log in the browser and asks Torn with the player's own key.
 */
import { logItems, type LogLike } from './crimes-report';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => {
  const v = typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x.trim()) ? Number(x) : x;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};
const posInt = (x: unknown): number | null => {
  const n = num(x);
  return n !== null && Number.isInteger(n) && n > 0 ? n : null;
};
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);
const first = (d: Obj, keys: readonly string[]): unknown => {
  for (const k of keys) if (d[k] !== undefined && d[k] !== null) return d[k];
  return undefined;
};

export type Venue = 'market' | 'bazaar';
export type Side = 'buy' | 'sell';

export const VENUE_NAME: Record<Venue, string> = { market: 'Item Market', bazaar: 'Bazaar' };

/** One item bought or sold, from one log line (a line with several items gives one each). */
export interface TradeLine {
  /** The log line's id (+ `#n` for its later items). */
  id: string;
  at: number;
  venue: Venue;
  side: Side;
  itemId: number;
  qty: number;
  /** Price of one (null: the line doesn't say). */
  each: number | null;
  /** What changed hands before any fee (null: unknown). */
  total: number | null;
  /** The Item Market's fee on a sale (0 when the line has none). */
  fee: number;
  /** The other player's id (null: anonymous or not said). */
  party: number | null;
  title: string;
}

/** Is this one of the log's Item Market or bazaar buy/sell lines, and which? (Listing, removing, editing aren't trades.) */
export function tradeKind(title: string): { venue: Venue; side: Side } | null {
  const t = title.trim().toLowerCase();
  const venue: Venue | null = /\bitem\s*market\b|\bitemmarket\b/.test(t) ? 'market' : /\bbazaar/.test(t) ? 'bazaar' : null;
  if (!venue) return null;
  if (/\b(add|remove|edit|cancel|refund)/.test(t)) return null;
  const side: Side | null = /\b(buy|bought|purchase)/.test(t) ? 'buy' : /\b(sell|sold|sale)/.test(t) ? 'sell' : null;
  return side ? { venue, side } : null;
}

/** A log line → the trades in it (none when it isn't an Item Market or bazaar buy/sell). */
export function readTradeLine(l: LogLike): TradeLine[] {
  const kind = tradeKind(l.title);
  if (!kind) return [];
  const d = l.data ?? {};
  const rawItems = first(d, ['items', 'item', 'items_bought', 'items_sold', 'item_id']);
  let items = logItems(rawItems).filter((i) => Number.isInteger(i.id) && i.amount > 0);
  // A lone item id with its amount beside it ({ item: 206, quantity: 3 }).
  const qtySaid = posInt(first(d, ['quantity', 'qty', 'amount']));
  if (items.length === 1 && qtySaid && num(rawItems) !== null) items = [{ id: items[0]!.id, amount: qtySaid }];
  if (!items.length) return [];
  const units = items.reduce((s, i) => s + i.amount, 0);
  const totalSaid = num(first(d, ['cost_total', 'total_cost', 'total', 'total_price', 'cost']));
  const eachSaid = num(first(d, ['cost_each', 'price_each', 'price']));
  const each = eachSaid !== null && eachSaid >= 0 ? eachSaid : totalSaid !== null && units ? totalSaid / units : null;
  const fee = Math.max(0, num(first(d, ['fee', 'tax', 'market_fee', 'fees'])) ?? 0);
  const party = posInt(first(d, kind.side === 'buy' ? ['seller', 'seller_id', 'user', 'player'] : ['buyer', 'buyer_id', 'user', 'player']));
  return items.map((it, n) => {
    const share = units ? it.amount / units : 1;
    const total = items.length === 1 && totalSaid !== null ? totalSaid : each !== null ? each * it.amount : null;
    return { id: n ? `${l.id}#${n}` : l.id, at: l.at, venue: kind.venue, side: kind.side, itemId: it.id, qty: it.amount, each, total, fee: Math.round(fee * share), party, title: l.title.trim() };
  });
}

/** Lines of one venue (or both: null), one side (or both), one item (or all: null). */
export function filterTrades(lines: readonly TradeLine[], f: { venue?: Venue | null; side?: Side | null; itemId?: number | null }): TradeLine[] {
  return lines.filter((l) => (!f.venue || l.venue === f.venue) && (!f.side || l.side === f.side) && (!f.itemId || l.itemId === f.itemId));
}

/** What a line moved in cash: a sale brings in its total less the fee, a buy costs its total (null: unknown). */
export const cashOf = (l: TradeLine): number | null => (l.total === null ? null : l.side === 'sell' ? l.total - l.fee : -l.total);

export interface ItemLedger {
  itemId: number;
  bought: number;
  spent: number;
  /** Bought units whose price isn't known. */
  boughtUnknown: number;
  sold: number;
  /** Sales before fees, the fees, and what came in. */
  gross: number;
  fees: number;
  got: number;
  soldUnknown: number;
  avgBuy: number | null;
  /** Average sale price after fees. */
  avgSell: number | null;
  /**
   * Profit on what was sold, each sold unit costed at the average price you bought it for in the same dates (FIFO
   * matching comes with S27). Null when nothing was bought (or sold) with a known price in the dates.
   */
  profit: number | null;
  /** Sold units without a buy price in the dates (their profit isn't counted). */
  uncosted: number;
  /** Lines (each a buy or a sale). */
  lines: number;
  last: number;
}

/** Per item: bought, sold, fees, averages and profit, busiest first. */
export function ledgerByItem(lines: readonly TradeLine[]): ItemLedger[] {
  const by = new Map<number, ItemLedger>();
  for (const l of lines) {
    const r = by.get(l.itemId) ?? { itemId: l.itemId, bought: 0, spent: 0, boughtUnknown: 0, sold: 0, gross: 0, fees: 0, got: 0, soldUnknown: 0, avgBuy: null, avgSell: null, profit: null, uncosted: 0, lines: 0, last: 0 };
    r.lines++;
    r.last = Math.max(r.last, l.at);
    if (l.side === 'buy') {
      if (l.total === null) r.boughtUnknown += l.qty;
      else (r.bought += l.qty), (r.spent += l.total);
    } else if (l.total === null) r.soldUnknown += l.qty;
    else (r.sold += l.qty), (r.gross += l.total), (r.fees += l.fee), (r.got += l.total - l.fee);
    by.set(l.itemId, r);
  }
  for (const r of by.values()) {
    r.avgBuy = r.bought ? r.spent / r.bought : null;
    r.avgSell = r.sold ? r.got / r.sold : null;
    if (r.sold && r.avgBuy !== null) r.profit = Math.round(r.got - r.sold * r.avgBuy);
    else r.uncosted = r.sold;
  }
  return [...by.values()].sort((a, b) => b.spent + b.gross - (a.spent + a.gross) || a.itemId - b.itemId);
}

export interface TradeTotals {
  buys: number;
  bought: number;
  spent: number;
  sales: number;
  sold: number;
  gross: number;
  fees: number;
  got: number;
  /** What came in less what went out (known amounts only). */
  cash: number;
  /** Sum of the items' profits that can be worked out. */
  profit: number;
  /** Sold units whose profit can't be worked out (no buy in the dates). */
  uncosted: number;
  /** Lines without a price. */
  unknown: number;
}

export function tradeTotals(lines: readonly TradeLine[]): TradeTotals {
  const t: TradeTotals = { buys: 0, bought: 0, spent: 0, sales: 0, sold: 0, gross: 0, fees: 0, got: 0, cash: 0, profit: 0, uncosted: 0, unknown: 0 };
  for (const l of lines) {
    if (l.total === null) t.unknown++;
    if (l.side === 'buy') {
      t.buys++;
      t.bought += l.qty;
      t.spent += l.total ?? 0;
    } else {
      t.sales++;
      t.sold += l.qty;
      t.gross += l.total ?? 0;
      t.fees += l.total === null ? 0 : l.fee;
      t.got += l.total === null ? 0 : l.total - l.fee;
    }
  }
  t.cash = t.got - t.spent;
  for (const r of ledgerByItem(lines)) {
    t.profit += r.profit ?? 0;
    t.uncosted += r.uncosted;
  }
  return t;
}

/** The ledger as CSV rows (log-kit `toCsv`). */
export function tradesCsvRows(lines: readonly TradeLine[], name: (id: number) => string): { header: string[]; rows: Array<Array<string | number | null>> } {
  return {
    header: ['time_utc', 'venue', 'side', 'item_id', 'item', 'quantity', 'price_each', 'total', 'fee', 'cash', 'other_player_id', 'log_title'],
    rows: lines.map((l) => [new Date(l.at).toISOString(), VENUE_NAME[l.venue], l.side, l.itemId, name(l.itemId), l.qty, l.each, l.total, l.fee, cashOf(l), l.party, l.title]),
  };
}

// ---------------------------------------------------------------- your listings and your bazaar now

export interface MyListing {
  /** Torn's listing id (Item Market) or the item's uid (bazaar), else the item id — unique in the list. */
  key: string;
  venue: Venue;
  itemId: number;
  name: string | null;
  type: string | null;
  /** Price of one. */
  price: number;
  amount: number;
  /** Torn's market value / average price, when the answer carries it. */
  value: number | null;
  anonymous: boolean;
}

/** Torn v2 `/user/itemmarket` → your Item Market listings (cheapest first per item). Null when it isn't that answer. */
export function readMyItemMarket(body: unknown): MyListing[] | null {
  const b = obj(body);
  const list = b?.itemmarket;
  if (!b || !Array.isArray(list)) return null;
  return list
    .map((x, i): MyListing | null => {
      const l = obj(x);
      const item = obj(l?.item);
      const itemId = posInt(item?.id ?? l?.item_id);
      const price = num(l?.price);
      const amount = posInt(l?.available ?? l?.amount) ?? posInt(l?.amount);
      if (!l || !itemId || price === null || price <= 0 || !amount) return null;
      const avg = num(l.average_price ?? item?.average_price);
      return { key: `m${posInt(l.id) ?? `${itemId}-${i}`}`, venue: 'market', itemId, name: str(item?.name), type: str(item?.type), price, amount, value: avg && avg > 0 ? avg : null, anonymous: l.is_anonymous === true };
    })
    .filter((l): l is MyListing => !!l)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '') || a.itemId - b.itemId || a.price - b.price);
}

/** Torn v1 `/user/?selections=bazaar` (or v2's `bazaar` list) → your bazaar's stock. Null when it isn't that answer. */
export function readMyBazaar(body: unknown): MyListing[] | null {
  const b = obj(body);
  if (!b || !('bazaar' in b)) return null;
  const raw = Array.isArray(b.bazaar) ? b.bazaar : obj(b.bazaar) ? Object.values(b.bazaar as Obj) : [];
  return raw
    .map((x, i): MyListing | null => {
      const l = obj(x);
      const itemId = posInt(l?.ID ?? l?.id ?? l?.item_id);
      const price = num(l?.price);
      const amount = posInt(l?.quantity ?? l?.amount);
      if (!l || !itemId || price === null || price <= 0 || !amount) return null;
      const mv = num(l.market_price ?? l.market_value);
      const uid = posInt(l.UID ?? l.uid);
      return { key: `b${uid ?? `${itemId}-${i}`}`, venue: 'bazaar', itemId, name: str(l.name), type: str(l.type), price, amount, value: mv && mv > 0 ? mv : null, anonymous: false };
    })
    .filter((l): l is MyListing => !!l)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '') || a.itemId - b.itemId || a.price - b.price);
}

export interface VsCheapest {
  /** 'cheapest': nobody lists it for less (green); 'beaten': someone does (red); 'unknown': nothing to compare. */
  state: 'cheapest' | 'beaten' | 'unknown';
  /** Your price less the cheapest (positive: you're dearer). */
  diff: number | null;
  /** …in percent of the cheapest. */
  pct: number | null;
}

/** Your price against the cheapest Item Market listing now (yours may be that listing: a tie is still the cheapest). */
export function vsCheapest(price: number, cheapest: number | null): VsCheapest {
  if (cheapest === null || !(cheapest > 0)) return { state: 'unknown', diff: null, pct: null };
  const diff = price - cheapest;
  return { state: diff <= 0 ? 'cheapest' : 'beaten', diff, pct: Math.round((diff / cheapest) * 1000) / 10 };
}

/** What your listings are worth at your prices, and how many units. */
export function listingsWorth(list: readonly MyListing[]): { units: number; worth: number; value: number | null } {
  let units = 0;
  let worth = 0;
  let value = 0;
  let valued = true;
  for (const l of list) {
    units += l.amount;
    worth += l.price * l.amount;
    if (l.value === null) valued = false;
    else value += l.value * l.amount;
  }
  return { units, worth, value: valued && list.length ? value : null };
}
